import type { FormEvent } from 'react';
import { useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import type { TagInputValue } from '@hotjar/hotsauce';
import { Button, Field, Link, Stack, TagInput, Text, tokens } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { validateEmail } from '@hotjar/webapp/lib/email';
import { useMachine } from '@xstate/react';
import type { DoneInvokeEvent } from 'xstate';
import { useEffectOnceWithDependencies } from '@hotjar/webapp/hooks/useEffectOnceWithDependencies';
import { FlatCard } from '~/subscription-management/components';
import NotificationsSvg from '../../resources/images/notifications.svg';
import { trackClick, INVOICES_PAGE, FORM_NAME, trackFormSubmit } from '../../utils/tracking';
import { billingNotificationsMachine } from '~/subscription-management/shared-state-machines/billing-notifications.machine';
import type {
  Customer,
  CustomerEmailAddresses,
} from '~/subscription-management/api/common/model/customer';

type StyleProps = { narrow: boolean };

interface ExternalButton {
  formId: string;
  setLoading: (isLoading: boolean) => void;
  setDisabled: (isDisabled: boolean) => void;
  setTouched?: (isTouched: boolean) => void;
}

export type Props = {
  accountId: number;
  userId: number;
  className?: string;
  onUpdate?: (emails: Customer['additionalEmailAddresses']) => void;
  externalButton?: ExternalButton;
  shouldSkipViewingState?: boolean;
} & Partial<StyleProps>;

const S = {
  Card: styled(FlatCard)<StyleProps>`
    display: flex;
    flex-direction: row;
    gap: ${tokens.spacer.x1};
    align-items: flex-start;
    ${(p) =>
      p.narrow &&
      css`
        flex-direction: column;
        ${tokens.elevation(2)};
      `}
  `,
  Illustration: styled.img<StyleProps>`
    height: ${(p) => (p.narrow ? '60px' : '100px')};
    padding-right: ${tokens.spacer.x3};
  `,
  Content: styled.div`
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacer.x2};
    flex: 1;
  `,
  Form: styled.form<StyleProps>`
    display: flex;
    gap: ${tokens.spacer.x1};
    align-items: flex-end;

    ${(p) =>
      p.narrow &&
      css`
        flex-direction: column;
      `}
  `,
  Field: styled(Field)`
    width: 100%;
  `,
  Title: styled(Text)<StyleProps>`
    ${(p) => (p.narrow ? tokens.text('subtitle2') : tokens.text('header5'))};
  `,
  Description: styled(Text)<StyleProps>`
    ${(p) => (p.narrow ? tokens.text('caption') : tokens.text('body2'))};
  `,
};

export function InvoiceNotifications({
  accountId,
  userId,
  onUpdate = () => null,
  externalButton,
  className,
  shouldSkipViewingState = false,
  narrow = false,
}: Props): JSX.Element {
  const [state, send] = useMachine(billingNotificationsMachine, {
    devTools: process.env.NODE_ENV === 'development',
    context: {
      accountId,
      userId,
      shouldSkipViewingState,
    },
    actions: {
      onUpdate: (_, { data }: DoneInvokeEvent<CustomerEmailAddresses>) => {
        onUpdate(data?.additionalEmailAddresses);
      },
    },
  });

  const { t } = useTranslationWithPrefix('checkout:invoices.notifications');
  const { error, additionalEmailAddresses } = state.context;
  const emailAddresses = additionalEmailAddresses ?? [];
  const [emails, setEmails] = useState<TagInputValue[]>(
    emailAddresses.map((address) => ({ name: address })),
  );
  const [isInvalid, setIsInvalid] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isEditing = state.matches('editing') || state.matches('update');
  const isLoading = state.matches('update') || state.matches('loading');

  useEffect(() => {
    externalButton?.setLoading(isLoading);
    if (!isLoading) {
      setEmails(emailAddresses.map((email) => ({ name: email })));
    }
  }, [emailAddresses, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffectOnceWithDependencies(
    () => {
      inputRef.current?.addEventListener(
        'input',
        () => {
          if (externalButton?.setTouched) {
            externalButton.setTouched(true);
          }
        },
        { once: true },
      );
    },
    [inputRef.current],
    () => Boolean(inputRef.current) && Boolean(externalButton?.setTouched),
  );

  const handleInvalid = (invalid: boolean) => {
    setIsInvalid(invalid);
    externalButton?.setDisabled(invalid);
  };

  const areEmailsValid = (values: TagInputValue[]) =>
    values.every((value) => validateEmail(value.name));

  const handleEditClick = () => {
    trackClick({
      eventName: INVOICES_PAGE.edit,
      uiPosition: 'Billing notification emails',
      accountId,
    });
    send('EDIT_NOTIFICATIONS');
  };

  const trackInvalidFormSubmit = () =>
    trackFormSubmit({
      name: FORM_NAME.billingNotifications,
      submitSuccess: false,
      accountId,
      errorCode: 'InvalidFormat',
      errorMessage: t('error-input'),
    });

  const handleChange = (mails: TagInputValue[]) => {
    const containsInvalidEmails = !areEmailsValid(mails);
    handleInvalid(containsInvalidEmails);
    setEmails(mails);
    if (containsInvalidEmails) {
      trackInvalidFormSubmit();
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const input = inputRef.current?.querySelector('input');
    const emailsToSubmit = emails.map(({ name }) => name);

    if (input?.value !== undefined && input?.value !== '') {
      const newEmail = input.value;
      setEmails((prevEmails) => [...prevEmails, { name: newEmail }]);
      input.value = '';

      if (validateEmail(newEmail)) {
        emailsToSubmit.push(newEmail);
      } else {
        trackInvalidFormSubmit();
        handleInvalid(true);
        return;
      }
    }

    send({ type: 'UPDATE_NOTIFICATIONS', additionalEmailAddresses: emailsToSubmit });
  };

  const getErrorMessage = (): string | undefined => {
    if (isInvalid) {
      return t('error-input');
    }
    return error === true ? t('error-generic') : undefined;
  };

  return (
    <S.Card className={className} narrow={narrow}>
      <S.Illustration src={NotificationsSvg} alt="" narrow={narrow} />

      <S.Content>
        <Stack direction="column" spacing="x1">
          <S.Title as="h2" narrow={narrow}>
            {t('title')}
          </S.Title>
          <S.Description id="notifications-label" textColor="text_light" narrow={narrow}>
            {t(isEditing ? 'label' : 'description')}
          </S.Description>
        </Stack>

        {isEditing ? (
          <S.Form onSubmit={handleSubmit} narrow={narrow} id={externalButton?.formId}>
            <S.Field
              labelText=""
              invalidText={getErrorMessage()}
              isInvalid={isInvalid || error}
              inputId="emails"
            >
              <TagInput
                ref={inputRef}
                value={emails}
                onChange={handleChange}
                placeholder={t('placeholder')}
                id="emails"
                isInvalid={isInvalid}
                customTrigger=" "
                size={narrow ? 'm' : 'l'}
                aria-labelledby="notifications-label"
              />
            </S.Field>
            {externalButton === undefined && (
              <Button
                type="submit"
                kind="secondary"
                isLoading={isLoading}
                isDisabled={isInvalid}
                loadingText={t('saving')}
                size={narrow ? 's' : 'm'}
              >
                {t('save')}
              </Button>
            )}
          </S.Form>
        ) : (
          <Text textColor="text_light">{emailAddresses.join(', ')}</Text>
        )}
      </S.Content>

      {!isEditing && (
        <Link as="button" kind="standalone" onClick={handleEditClick}>
          {t('edit')}
        </Link>
      )}
    </S.Card>
  );
}
